/**
 * Procedural placeholder art for the platformer board — split out of
 * PlatformerBoard.js (which was over the 1000-line limit) along the
 * rendering/orchestration seam. Pure draw functions: every export
 * takes plain data + sizes and returns / fills Pixi nodes; no board
 * state is touched, so PlatformerBoard stays the single orchestrator.
 *
 *   drawTilePlaceholder    — stylised tile art (bricks, slopes, decor)
 *   drawEntityPlaceholder  — stylised entity art (player, goomba, …)
 *   paintAreaPlaceholder   — editor overlay for area.trigger zones
 *   paintCameraZonePlaceholder — editor overlay for camera.zone
 *   paintLightPlaceholder  — editor overlay for light.point
 *   paintPlatformPlaceholder — editor overlay for platform.moving
 *                              (slab + travel path + ghost endpoint)
 */

import { Graphics, Text, TextStyle } from '../../../vendor/pixi.min.mjs';

/**
 * Procedural tile fallback when the user hasn't uploaded a PNG.
 * Each shape is a stylised version of the iconic Mario tile so
 * the editor is usable even before any art arrives.
 *
 * `mask` (autotiled tiles only) is the 4-bit neighbour mask; we
 * shade the EXPOSED edges so a painted blob of ground reads as one
 * connected mass with a lit top and shadowed sides, even before
 * the user imports a 4×4 autotile template.
 *
 * `rot` matters for slopes only: odd = mirrored (rises left).
 */
export function drawTilePlaceholder(type, def, w, h, mask = null, rot = 0) {
    const g = new Graphics();
    const c = def.fallbackColor ?? 0x9ca3af;
    // Slope wedge — filled polygon matching the EXACT surface line
    // the physics resolves (see slopeFloorY), with a lit top edge
    // so the walkable surface reads clearly.
    if (def.slope) {
        const mirrored = (rot % 2) === 1;
        const k = def.slope.kind;
        const hL = k === '45' ? 0 : k === '22lo' ? 0 : 0.5;   // left-edge height (rising right)
        const hR = k === '45' ? 1 : k === '22lo' ? 0.5 : 1;   // right-edge height
        const leftH  = (mirrored ? hR : hL) * h;
        const rightH = (mirrored ? hL : hR) * h;
        g.poly([
            { x: 0, y: h - leftH },
            { x: w, y: h - rightH },
            { x: w, y: h },
            { x: 0, y: h },
        ]);
        g.fill(c);
        g.moveTo(0, h - leftH);
        g.lineTo(w, h - rightH);
        g.stroke({ color: 0xffffff, width: 3, alpha: 0.4 });
        return g;
    }
    if (mask != null) {
        g.rect(0, 0, w, h);
        g.fill(c);
        const edge = Math.max(3, Math.round(h * 0.14));
        if (!(mask & 1)) { g.rect(0, 0, w, edge); g.fill({ color: 0xffffff, alpha: 0.35 }); }
        if (!(mask & 4)) { g.rect(0, h - edge, w, edge); g.fill({ color: 0x000000, alpha: 0.28 }); }
        if (!(mask & 8)) { g.rect(0, 0, edge, h); g.fill({ color: 0xffffff, alpha: 0.14 }); }
        if (!(mask & 2)) { g.rect(w - edge, 0, edge, h); g.fill({ color: 0x000000, alpha: 0.16 }); }
        return g;
    }
    // Block-style tiles get a beveled chunky rectangle with an
    // inner shading band so they feel tactile even without art.
    if (type.startsWith('block.') || type === 'brick' || type === 'ground' || type.startsWith('pipe.')) {
        g.rect(0, 0, w, h);
        g.fill(c);
        g.rect(0, 0, w, h);
        g.stroke({ color: 0x000000, width: 2, alpha: 0.45 });
        // Inner highlight for a 2.5D look.
        g.rect(3, 3, w - 6, Math.min(8, h * 0.18));
        g.fill({ color: 0xffffff, alpha: 0.18 });
        // Per-type accents
        if (type === 'block.question') {
            g.circle(w / 2, h / 2, Math.min(w, h) * 0.18);
            g.fill(0x000000);
            // Question dot
            g.circle(w / 2, h * 0.74, 3);
            g.fill(0x000000);
        } else if (type === 'block.coin') {
            g.circle(w / 2, h / 2, Math.min(w, h) * 0.22);
            g.fill(0xfacc15);
            g.circle(w / 2, h / 2, Math.min(w, h) * 0.22);
            g.stroke({ color: 0x000000, width: 2, alpha: 0.6 });
        } else if (type === 'brick') {
            // Bricklines for visual texture.
            g.moveTo(0, h / 2); g.lineTo(w, h / 2);
            g.moveTo(w / 2, 0); g.lineTo(w / 2, h / 2);
            g.moveTo(w / 4, h / 2); g.lineTo(w / 4, h);
            g.moveTo((3 * w) / 4, h / 2); g.lineTo((3 * w) / 4, h);
            g.stroke({ color: 0x000000, width: 1.5, alpha: 0.35 });
        }
    } else if (type.startsWith('cloud.')) {
        // Cloud — three overlapping ellipses, white fill, soft outline.
        g.ellipse(w * 0.30, h * 0.55, w * 0.25, h * 0.40);
        g.ellipse(w * 0.55, h * 0.45, w * 0.30, h * 0.45);
        g.ellipse(w * 0.80, h * 0.55, w * 0.22, h * 0.38);
        g.fill(0xffffff);
        g.ellipse(w * 0.30, h * 0.55, w * 0.25, h * 0.40);
        g.ellipse(w * 0.55, h * 0.45, w * 0.30, h * 0.45);
        g.ellipse(w * 0.80, h * 0.55, w * 0.22, h * 0.38);
        g.stroke({ color: 0xfdfdfd, width: 2, alpha: 0.6 });
    } else if (type.startsWith('hill.')) {
        g.ellipse(w * 0.50, h, w * 0.50, h * 0.95);
        g.fill(c);
        g.ellipse(w * 0.30, h * 0.95, w * 0.10, h * 0.10);
        g.ellipse(w * 0.70, h * 0.95, w * 0.10, h * 0.10);
        g.fill(0xffffff);
    } else if (type.startsWith('bush.')) {
        g.ellipse(w * 0.25, h * 0.6, w * 0.22, h * 0.45);
        g.ellipse(w * 0.50, h * 0.5, w * 0.30, h * 0.55);
        g.ellipse(w * 0.78, h * 0.6, w * 0.22, h * 0.45);
        g.fill(c);
    } else if (type === 'castle') {
        g.rect(w * 0.10, h * 0.30, w * 0.80, h * 0.70);
        g.fill(c);
        // Battlements on top.
        for (let i = 0; i < 5; i++) {
            g.rect(w * (0.10 + 0.16 * i), h * 0.20, w * 0.10, h * 0.12);
            g.fill(c);
        }
        g.rect(w * 0.40, h * 0.55, w * 0.20, h * 0.45);
        g.fill(0x111827);
    } else if (type === 'pole.flag') {
        g.rect(w * 0.45, 0, w * 0.10, h);
        g.fill(0xfacc15);
        g.rect(w * 0.40, 0, w * 0.20, w * 0.10);
        g.fill(0xf59e0b);
    } else {
        g.rect(0, 0, w, h);
        g.fill(c);
    }
    return g;
}

/**
 * Procedural entity art for the editor when no PNG is configured —
 * the entity analogue of `drawTilePlaceholder`. Each shape is a
 * stylised, instantly-recognisable version of the character so the
 * designer can lay out a level before any art is imported.
 */
export function drawEntityPlaceholder(type, def, w, h) {
    const g = new Graphics();
    const cx = w / 2;
    const min = Math.min(w, h);

    if (type === 'player.spawn') {
        // Plumber-ish hero: blue overalls, red shirt + cap, round head.
        g.rect(w * 0.34, h * 0.74, w * 0.12, h * 0.20); g.fill(0x1e3a8a); g.rect(w * 0.54, h * 0.74, w * 0.12, h * 0.20); g.fill(0x1e3a8a);
        g.ellipse(w * 0.40, h * 0.95, w * 0.11, h * 0.05); g.fill(0x422006); g.ellipse(w * 0.60, h * 0.95, w * 0.11, h * 0.05); g.fill(0x422006);
        g.roundRect(w * 0.32, h * 0.46, w * 0.36, h * 0.34, w * 0.06); g.fill(0x2563eb);
        g.roundRect(w * 0.27, h * 0.44, w * 0.46, h * 0.14, w * 0.06); g.fill(0xdc2626);
        g.circle(cx, h * 0.33, min * 0.17); g.fill(0xf2c79b);
        g.ellipse(cx, h * 0.245, min * 0.20, min * 0.12); g.fill(0xdc2626);
        g.rect(w * 0.30, h * 0.235, w * 0.42, h * 0.05); g.fill(0xdc2626);
        g.circle(cx + w * 0.05, h * 0.33, 2.2); g.fill(0x111827);
    } else if (type === 'goomba') {
        // Brown mushroom enemy with feet + angry eyes.
        g.ellipse(cx, h * 0.44, w * 0.34, h * 0.32); g.fill(0x92400e);
        g.ellipse(cx, h * 0.60, w * 0.30, h * 0.22); g.fill(0xc98a52);
        g.ellipse(w * 0.36, h * 0.86, w * 0.13, h * 0.07); g.fill(0x5b2c0a); g.ellipse(w * 0.64, h * 0.86, w * 0.13, h * 0.07); g.fill(0x5b2c0a);
        g.ellipse(w * 0.40, h * 0.43, w * 0.07, h * 0.10); g.fill(0xffffff); g.ellipse(w * 0.60, h * 0.43, w * 0.07, h * 0.10); g.fill(0xffffff);
        g.circle(w * 0.42, h * 0.46, 2.2); g.fill(0x111827); g.circle(w * 0.58, h * 0.46, 2.2); g.fill(0x111827);
        g.moveTo(w * 0.33, h * 0.33); g.lineTo(w * 0.47, h * 0.40);
        g.moveTo(w * 0.67, h * 0.33); g.lineTo(w * 0.53, h * 0.40);
        g.stroke({ color: 0x111827, width: 2 });
    } else if (type === 'koopa') {
        // Green-shelled turtle, head poking out to the right.
        g.circle(w * 0.70, h * 0.40, min * 0.13); g.fill(0x86efac);
        g.circle(w * 0.74, h * 0.38, 2); g.fill(0x111827);
        g.ellipse(w * 0.36, h * 0.82, w * 0.10, h * 0.06); g.fill(0xfcd34d);
        g.ellipse(w * 0.58, h * 0.82, w * 0.10, h * 0.06); g.fill(0xfcd34d);
        g.ellipse(w * 0.46, h * 0.55, w * 0.30, h * 0.30); g.fill(0x16a34a);
        g.ellipse(w * 0.46, h * 0.55, w * 0.19, h * 0.19); g.fill(0xfde68a);
        g.ellipse(w * 0.46, h * 0.55, w * 0.30, h * 0.30);
        g.stroke({ color: 0x14532d, width: 2 });
    } else if (type === 'coin') {
        g.circle(cx, h * 0.5, min * 0.30); g.fill(0xfacc15);
        g.circle(cx, h * 0.5, min * 0.30); g.stroke({ color: 0xa16207, width: 2 });
        g.circle(cx, h * 0.5, min * 0.18); g.stroke({ color: 0xa16207, width: 1.5 });
        g.ellipse(w * 0.42, h * 0.40, w * 0.05, h * 0.08); g.fill({ color: 0xfffbeb, alpha: 0.8 });
    } else if (type === 'mushroom') {
        // Power-up: red cap with white spots on a pale stem.
        g.roundRect(w * 0.36, h * 0.52, w * 0.28, h * 0.34, w * 0.05); g.fill(0xfde68a);
        g.rect(w * 0.43, h * 0.66, w * 0.05, h * 0.10); g.fill(0x111827); g.rect(w * 0.52, h * 0.66, w * 0.05, h * 0.10); g.fill(0x111827);
        g.ellipse(cx, h * 0.46, w * 0.36, h * 0.26); g.fill(0xdc2626);
        g.circle(w * 0.38, h * 0.42, w * 0.06); g.fill(0xffffff); g.circle(w * 0.62, h * 0.42, w * 0.06); g.fill(0xffffff);
        g.circle(cx, h * 0.33, w * 0.05); g.fill(0xffffff);
    } else if (type === 'flag.goal') {
        g.rect(w * 0.46, h * 0.08, w * 0.08, h * 0.84); g.fill(0xd1d5db);
        g.circle(cx + w * 0.005, h * 0.08, w * 0.09); g.fill(0xfacc15);
        g.poly([w * 0.54, h * 0.14, w * 0.88, h * 0.26, w * 0.54, h * 0.38]); g.fill(0x22c55e);
        g.rect(w * 0.32, h * 0.88, w * 0.36, h * 0.08); g.fill(0x6b7280);
    } else if (type === 'checkpoint') {
        g.rect(w * 0.30, h * 0.12, w * 0.07, h * 0.80); g.fill(0x6b7280);
        g.poly([w * 0.37, h * 0.16, w * 0.80, h * 0.27, w * 0.37, h * 0.38]); g.fill(0x22d3ee);
        g.rect(w * 0.22, h * 0.88, w * 0.30, h * 0.07); g.fill(0x475569);
    } else if (type === 'note') {
        // Eighth-note glyph: disc head + stem + flag sweep. Pink by
        // default; matches the in-game NoteEntity's procedural look.
        const c = def.fallbackColor ?? 0xf472b6;
        g.rect(w * 0.55, h * 0.12, Math.max(3, w * 0.07), h * 0.55); g.fill(c);
        g.poly([w * 0.62, h * 0.12, w * 0.86, h * 0.22, w * 0.66, h * 0.34, w * 0.62, h * 0.30]); g.fill(c);
        g.ellipse(w * 0.36, h * 0.72, min * 0.22, min * 0.16); g.fill(c);
        g.ellipse(w * 0.36, h * 0.72, min * 0.22, min * 0.16); g.stroke({ color: 0x1f2937, width: 1.5, alpha: 0.6 });
    } else {
        g.circle(cx, h * 0.5, min * 0.35); g.fill({ color: def.fallbackColor ?? 0x9ca3af, alpha: 0.85 });
        g.circle(cx, h * 0.5, min * 0.35); g.stroke({ color: 0x000000, width: 2, alpha: 0.6 });
    }
    return g;
}

/**
 * Editor render for an Area / Trigger zone: a tinted, dashed-ish
 * rectangle sized by the entity's w/h props, with an effect label.
 * The fill colour reads the effect so hurt zones look dangerous
 * (red), goal zones gold, etc.
 */
export function paintAreaPlaceholder(layerC, e, cs) {
    const wCells = Math.max(1, Math.round(e.props?.w || 2));
    const hCells = Math.max(1, Math.round(e.props?.h || 2));
    const x = e.c * cs;
    const y = e.r * cs;
    const w = wCells * cs;
    const h = hCells * cs;
    const effect = e.props?.effect || 'hurt';
    const colors = { hurt: 0xef4444, coin: 0xfacc15, grow: 0x22c55e, win: 0xfbbf24, none: 0x94a3b8 };
    const color = colors[effect] ?? 0xa855f7;

    const g = new Graphics();
    g.rect(0, 0, w, h);
    g.fill({ color, alpha: 0.16 });
    g.rect(1, 1, w - 2, h - 2);
    g.stroke({ color, width: 2, alpha: 0.9 });
    g.x = x;
    g.y = y;
    layerC.addChild(g);

    // Effect label in the top-left corner of the zone.
    const label = new Text({
        text: effect.toUpperCase(),
        style: new TextStyle({
            fontFamily: 'Poppins, Arial, sans-serif',
            fontSize: Math.min(14, cs * 0.32), fontWeight: '800',
            fill: color,
        }),
    });
    label.x = x + 4;
    label.y = y + 3;
    layerC.addChild(label);
}

/**
 * Editor-only: draw a Camera Zone as a labelled rectangle (a dashed-feel
 * cyan box with a crosshair at its centre + a "ZOOM ×N" tag) so the
 * scripted-zoom region is visible and clickable. Pure visual — the
 * runtime CameraZoneEntity never renders.
 */
export function paintCameraZonePlaceholder(layerC, e, cs) {
    const wCells = Math.max(1, Math.round(e.props?.w || 6));
    const hCells = Math.max(1, Math.round(e.props?.h || 4));
    const x = e.c * cs;
    const y = e.r * cs;
    const w = wCells * cs;
    const h = hCells * cs;
    const color = 0x38bdf8;
    const zoom = Number.isFinite(e.props?.zoom) ? e.props.zoom : 1.6;

    const g = new Graphics();
    g.rect(0, 0, w, h);
    g.fill({ color, alpha: 0.10 });
    g.rect(1, 1, w - 2, h - 2);
    g.stroke({ color, width: 2, alpha: 0.9 });
    // Centre crosshair = the framing target.
    const ccx = w / 2, ccy = h / 2;
    const m = Math.min(w, h) * 0.12;
    g.moveTo(ccx - m, ccy); g.lineTo(ccx + m, ccy);
    g.moveTo(ccx, ccy - m); g.lineTo(ccx, ccy + m);
    g.stroke({ color, width: 2, alpha: 0.8 });
    g.x = x;
    g.y = y;
    layerC.addChild(g);

    const tag = (zoom >= 1 ? `ZOOM IN ×${zoom.toFixed(2)}` : `ZOOM OUT ×${zoom.toFixed(2)}`)
        + (e.props?.focus === 'zone' ? ' · LOCK' : '');
    const label = new Text({
        text: tag,
        style: new TextStyle({
            fontFamily: 'Poppins, Arial, sans-serif',
            fontSize: Math.min(14, cs * 0.32), fontWeight: '800',
            fill: color,
        }),
    });
    label.x = x + 4;
    label.y = y + 3;
    layerC.addChild(label);
}

/**
 * Editor placeholder for a Point Light: a bright bulb at the cell centre
 * + a translucent reach ring sized by the `radius` prop, tinted with the
 * authored colour so designers can aim and size it against the level.
 */
export function paintLightPlaceholder(layerC, e, cs) {
    const cx = e.c * cs + cs / 2, cy = e.r * cs + cs / 2;
    const color = (typeof e.props?.color === 'string')
        ? (parseInt(e.props.color.replace('#', ''), 16) || 0xffd9a0) : 0xffd9a0;
    const radius = Number.isFinite(e.props?.radius) ? e.props.radius : 160;
    const g = new Graphics();
    g.circle(cx, cy, radius); g.fill({ color, alpha: 0.08 });
    g.circle(cx, cy, radius); g.stroke({ color, width: 1.5, alpha: 0.5 });
    g.circle(cx, cy, cs * 0.32); g.fill({ color, alpha: 0.9 });
    g.circle(cx, cy, cs * 0.15); g.fill({ color: 0xffffff, alpha: 0.95 });
    layerC.addChild(g);
}

/**
 * Editor placeholder for a Moving Platform: the slab at its spawn
 * position, the travel path to its far endpoint (dashed line with an
 * arrowhead), and a ghost slab at the destination so the designer can
 * read the full sweep at a glance. Mirrors the runtime visual
 * (MovingPlatformEntity's procedural slab).
 */
export function paintPlatformPlaceholder(layerC, e, cs) {
    const wCells = Math.max(1, Math.round(e.props?.w || 3));
    const w = wCells * cs;
    const h = Math.round(cs * 0.5);
    const x = e.c * cs;
    const y = e.r * cs;
    const dx = (Number.isFinite(e.props?.dx) ? e.props.dx : 4) * cs;
    const dy = (Number.isFinite(e.props?.dy) ? e.props.dy : 0) * cs;
    const color = 0x8b5cf6;

    const slab = (gx, gy, alpha) => {
        const g = new Graphics();
        g.roundRect(0, 0, w, h, 6);
        g.fill({ color: 0x57534e, alpha });
        g.roundRect(0, 0, w, Math.max(4, h * 0.28), 6);
        g.fill({ color, alpha });
        g.roundRect(0, 0, w, h, 6);
        g.stroke({ color: 0x292524, width: 2, alpha: alpha * 0.8 });
        g.x = gx;
        g.y = gy;
        layerC.addChild(g);
    };

    // Travel path: dashed centre-line + arrowhead at the far end.
    if (dx !== 0 || dy !== 0) {
        const g = new Graphics();
        const x0 = x + w / 2, y0 = y + h / 2;
        const x1 = x0 + dx,   y1 = y0 + dy;
        const dist = Math.hypot(dx, dy) || 1;
        const ux = dx / dist, uy = dy / dist;
        const dash = 10;
        for (let d = 0; d < dist; d += dash * 2) {
            const dEnd = Math.min(d + dash, dist);
            g.moveTo(x0 + ux * d,    y0 + uy * d);
            g.lineTo(x0 + ux * dEnd, y0 + uy * dEnd);
        }
        g.stroke({ color, width: 2, alpha: 0.8 });
        // Arrowhead.
        const ah = 9;
        g.poly([
            { x: x1, y: y1 },
            { x: x1 - ux * ah - uy * ah * 0.6, y: y1 - uy * ah + ux * ah * 0.6 },
            { x: x1 - ux * ah + uy * ah * 0.6, y: y1 - uy * ah - ux * ah * 0.6 },
        ]);
        g.fill({ color, alpha: 0.9 });
        layerC.addChild(g);
        // Ghost slab at the destination.
        slab(x + dx, y + dy, 0.3);
    }

    slab(x, y, 1);
}
